import { useTranslation } from 'react-i18next';
import {
  Bed,
  BedDouble,
  Sofa,
  Baby,
  Users,
  ChevronDown,
} from 'lucide-react';
import { formatCurrency } from '@/lib/currency';
import type { SleepingConfig, RoomConfig } from '../amenities/BedConfigurator';
import { extraBedTypeCounts } from '../amenities/BedConfigurator';

interface SleepingCardProps {
  sleeping: SleepingConfig;
  darkMode: boolean;
  onViewDetails?: () => void;
  currency?: string;
}

// Room type icons
const ROOM_ICONS: Record<string, typeof Bed> = {
  bedroom: Bed,
  master_bedroom: BedDouble,
  guest_bedroom: Bed,
  kids_room: Baby,
  living_room: Sofa,
  loft: Bed,
  alcove: Bed,
  mezzanine: Bed,
  studio: Bed,
  studio_area: Bed,
};

export function SleepingCard({ sleeping, darkMode, onViewDetails: _onViewDetails, currency }: SleepingCardProps) {
  void _onViewDetails; // Reserved for future modal details
  const { t } = useTranslation();

  if (!sleeping || sleeping.rooms.length === 0) {
    return null;
  }

  // Calculate total capacity
  const calculateCapacity = (rooms: RoomConfig[]): number => {
    const BED_SLEEPS: Record<string, number> = {
      single: 1, twin: 2, double: 2, queen: 2, king: 2, super_king: 2,
      sofa_bed: 2, sofa_bed_single: 1, sofa_bed_double: 2,
      bunk: 2, bunk_bed: 2, crib: 1, toddler_bed: 1,
      floor_mattress: 1, air_mattress: 1, extra_mattress: 1,
      futon: 1, daybed: 1, murphy_bed: 2,
    };

    return rooms.reduce((total, room) => {
      return total + room.beds.reduce((roomTotal, bed) => {
        return roomTotal + (BED_SLEEPS[bed.type] || 1) * bed.count;
      }, 0);
    }, 0);
  };

  const totalCapacity = sleeping.totalCapacity || calculateCapacity(sleeping.rooms);

  return (
    <div
      className={`rounded-2xl overflow-hidden transition-all ${
        darkMode
          ? 'bg-slate-800 border border-slate-700 shadow-xl shadow-black/40 ring-1 ring-white/5'
          : 'bg-gradient-to-br from-white to-cyan-50/30 border border-cyan-100 shadow-xl shadow-gray-200/80'
      }`}
    >
      <div className="relative p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-2xl ${
              darkMode
                ? 'bg-gradient-to-br from-cyan-500 to-blue-600'
                : 'bg-gradient-to-br from-cyan-400 to-blue-500'
            } shadow-lg shadow-cyan-500/30`}>
              <Bed className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className={`font-bold text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {t('guest.sleeping.title', 'Kreveti i spavanje')}
              </h3>
              <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                {sleeping.rooms.length} {sleeping.rooms.length === 1
                  ? t('guest.sleeping.room', 'soba')
                  : t('guest.sleeping.rooms', 'sobe')}
              </p>
            </div>
          </div>

          {/* Capacity badge */}
          <div
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full ${
              darkMode
                ? 'bg-cyan-500/20 text-cyan-400'
                : 'bg-cyan-100 text-cyan-700'
            }`}
          >
            <Users className="w-4 h-4" />
            <span className="text-sm font-medium">{totalCapacity} {t('guest.sleeping.persons', 'osoba')}</span>
          </div>
        </div>

        {/* Rooms List */}
        <div className="space-y-2">
          {sleeping.rooms.map((room) => {
            const RoomIcon = ROOM_ICONS[room.type] || Bed;
            const roomCapacity = room.beds.reduce((total, bed) => {
              const BED_SLEEPS: Record<string, number> = {
                single: 1, twin: 2, double: 2, queen: 2, king: 2, super_king: 2,
                sofa_bed: 2, sofa_bed_single: 1, sofa_bed_double: 2,
                bunk: 2, bunk_bed: 2, crib: 1, toddler_bed: 1,
                floor_mattress: 1, air_mattress: 1, extra_mattress: 1,
                futon: 1, daybed: 1, murphy_bed: 2,
              };
              return total + (BED_SLEEPS[bed.type] || 1) * bed.count;
            }, 0);

            return (
              <div
                key={room.id}
                className={`flex items-center justify-between p-3 rounded-xl transition-all ${
                  darkMode
                    ? 'bg-slate-700/30 hover:bg-slate-700/50'
                    : 'bg-white hover:bg-cyan-50 shadow-sm'
                }`}
              >
                <div className="flex items-center gap-3">
                  <RoomIcon className={`w-5 h-5 ${darkMode ? 'text-cyan-400' : 'text-cyan-500'}`} />
                  <div>
                    <p className={`font-medium text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {room.name || t(`guest.sleeping.roomTypes.${room.type}`, room.type)}
                    </p>
                    <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                      ({roomCapacity} {roomCapacity === 1 ? t('guest.sleeping.person', 'osoba') : t('guest.sleeping.persons', 'osoba')})
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`flex items-center gap-1 text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    <Bed className="w-4 h-4" />
                    <span>{room.beds.reduce((total, bed) => total + bed.count, 0)}</span>
                  </div>
                  <ChevronDown className={`w-4 h-4 ${darkMode ? 'text-slate-400' : 'text-gray-400'}`} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Extra beds info */}
        {sleeping.extraBeds?.available && (
          <div
            className={`flex items-center gap-2 p-3 rounded-xl mt-3 ${
              darkMode
                ? 'bg-amber-500/10 border border-amber-500/20'
                : 'bg-amber-50 border border-amber-200'
            }`}
          >
            <Bed className={`w-4 h-4 mt-0.5 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} />
            <div className="flex-1">
              <p className={`text-sm font-medium ${darkMode ? 'text-amber-200' : 'text-amber-700'}`}>
                {t('guest.sleeping.extraBedsAvailable', 'Dodatni kreveti na zahtjev')}
              </p>
              {/* Per-type list with count + optional setup/usage note. */}
              {(() => {
                const perType = extraBedTypeCounts(sleeping.extraBeds);
                const typeNotes = sleeping.extraBeds?.typeNotes ?? {};
                const entries = Object.entries(perType);
                if (entries.length === 0) return null;
                return (
                  <ul className="mt-1 space-y-1">
                    {entries.map(([type, n]) => (
                      <li key={type} className={`text-xs ${darkMode ? 'text-amber-300/80' : 'text-amber-600'}`}>
                        <span className="font-medium">{n}× {t(`guest.sleeping.bedTypes.${type}`, type)}</span>
                        {typeNotes[type] && <span className="opacity-80"> — {typeNotes[type]}</span>}
                      </li>
                    ))}
                  </ul>
                );
              })()}
              {sleeping.extraBeds.notes && (
                <p className={`text-xs mt-1 ${darkMode ? 'text-amber-300/70' : 'text-amber-600'}`}>
                  {sleeping.extraBeds.notes}
                </p>
              )}
              {sleeping.extraBeds.price !== undefined && sleeping.extraBeds.price > 0 && (
                <p className={`text-xs font-medium mt-1 ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                  {formatCurrency(sleeping.extraBeds.price, currency)}/{t('common.night', 'noć')}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SleepingCard;
